import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { PRODUCTS, PRODUCT_CATEGORIES } from "@/data/products";
import { ArrowRight, CheckCircle2, Smartphone } from "lucide-react";
import SEO from "@/components/SEO";
import BrandedSim from "@/components/BrandedSim";

const Products = () => {
  const [active, setActive] = useState("all");
  const filtered = useMemo(() => active === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === active), [active]);

  return (
    <div data-testid="products-page">
      <SEO
        path="/products"
        title="Multi-Network SIMs, eSIMs & IoT Hardware"
        description="PTT NZ hardware: multi-network SIMs & eSIMs, rugged IoT terminals, PTT radios, ATEX-rated handsets and evidence-grade body cameras for enterprise fleets."
        keywords="multi-network eSIM Australia, rugged IoT hardware NZ AU, industrial PDA scanner, dispatch tablet New Zealand, enterprise IoT hardware NZ AU, government fleet hardware NZ, PTT radios New Zealand, PTT radios Australia, Push to Talk over Cellular device, ATEX radio New Zealand, body camera New Zealand, body camera Australia"
      />
      {/* Header */}
      <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-12">
          <div className="eyebrow">Hardware we support</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
            Devices engineered for <span className="text-[var(--ptt-teal-deep)]">mission-critical</span> work.
          </h1>
          <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl">
            A curated range of PTT radios, on-body cameras, IoT terminals and multi-network SIMs — all designed to run over our global multi-network SIM platform. Purchasing, plans and device management happen inside our <a href="https://portal.pttnz.net" target="_blank" rel="noopener noreferrer" className="signal-underline font-semibold">Connectivity Portal</a>.
          </p>
          <div className="flex flex-wrap gap-2 mt-9" data-testid="product-category-filters">
            <button
              onClick={() => setActive("all")}
              data-testid="filter-all"
              className={`px-4 py-2 text-sm font-medium border transition-colors ${active === "all" ? "bg-[var(--ptt-ink)] text-white border-[var(--ptt-ink)]" : "border-[var(--ptt-line)] hover:border-[var(--ptt-ink)]"}`}
            >All</button>
            {PRODUCT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-4 py-2 text-sm font-medium border transition-colors ${active === c.id ? "bg-[var(--ptt-ink)] text-white border-[var(--ptt-ink)]" : "border-[var(--ptt-line)] hover:border-[var(--ptt-ink)]"}`}
              >{c.label}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
        <div className="border-2 border-[var(--ptt-teal-deep)] bg-white p-6 lg:p-7 mb-10 flex flex-col sm:flex-row gap-5 items-start sm:items-center justify-between" data-testid="mdm-cross-link-banner">
          <div className="flex gap-4 items-start">
            <div className="w-11 h-11 shrink-0 grid place-items-center bg-[var(--ptt-teal)]/15">
              <Smartphone size={22} className="text-[var(--ptt-teal-deep)]" />
            </div>
            <div>
              <div className="eyebrow">Native platform capability</div>
              <h3 className="font-display text-lg font-bold mt-1 leading-snug">Every Android device here can be enrolled in MDM.</h3>
              <p className="text-sm text-[var(--ptt-muted)] mt-1.5 leading-relaxed max-w-xl">Remote lock &amp; wipe, kiosk lockdown, GPS tracking and custom APN loading — built right into your portal, no extra software required.</p>
            </div>
          </div>
          <Link to="/solutions/android-mdm" data-testid="mdm-cross-link-cta" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2 shrink-0 whitespace-nowrap">Explore Android MDM <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
          {filtered.map((p) => {
            const isSim = p.id === "multi-sim";
            const isEsim = p.id === "esim";
            return (
            <article key={p.id} id={p.category} className="bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] p-5 flex flex-col transition-colors" data-testid={`product-card-${p.id}`}>
              <div className="aspect-[4/3] bg-[var(--ptt-paper)] grid place-items-center overflow-hidden p-4">
                {isSim || isEsim ? (
                  <BrandedSim variant={isEsim ? "esim" : "sim"} className="w-full h-auto" />
                ) : (
                  <img src={p.image} alt={p.name} className="max-w-[78%] max-h-[78%] object-contain" />
                )}
              </div>
              <div className="font-mono-tech text-[10px] tracking-widest uppercase text-[var(--ptt-muted)] mt-5">
                {PRODUCT_CATEGORIES.find((c) => c.id === p.category)?.label}
              </div>
              <h3 className="font-display text-xl font-bold leading-snug mt-2">{p.name}</h3>
              <p className="text-sm text-[var(--ptt-muted)] mt-2">{p.tagline}</p>
              <ul className="mt-4 space-y-1.5 text-sm">
                {p.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--ptt-teal-deep)] mt-1 shrink-0" /> <span>{h}</span></li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-[var(--ptt-line)] flex items-center justify-between">
                <span className="font-mono-tech text-[11px] tracking-widest text-[var(--ptt-muted)]">PTT-NZ · GLOBAL SIM</span>
                <Link to="/contact" data-testid={`product-cta-${p.id}`} className="text-sm font-semibold inline-flex items-center gap-1.5 signal-underline">Enquire <ArrowRight size={14} /></Link>
              </div>
            </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Products;
